import { Injectable } from '@angular/core';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { LinqFeatureFlagService } from '@linq/feature-flag';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  share,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  throttleTime,
  timeout,
  timer,
  withLatestFrom,
} from 'rxjs';
import { ConfigService } from '@app/core/services/config/config.service';
import { ClaimsEnum, ClaimsValueEnum, FeatureFlagEnum } from '@app/enums';
import { LoadingService } from '@app/shared/services/loading.service';
import { FramesService } from '../frames';
import { SessionActivityService } from '../session-activity';

// How far ahead of access-token expiry to trigger a proactive renewal.
const REFRESH_LEAD_SECONDS = 1800;

export interface User {
  id: string;
  name: string;
  email: string;
  admin: boolean;
  role: string;
  sub?: string;
  dbUserId?: string;
  tenantId?: string;
  subdomain?: string;
  isAuth0?: boolean;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private readonly auth0: Auth0Service,
    private readonly featureFlagService: LinqFeatureFlagService,
    private readonly configService: ConfigService,
    private readonly framesService: FramesService,
    private readonly loadingService: LoadingService,
    private readonly sessionActivityService: SessionActivityService,
  ) {
    window.authService = this;

    // Initialize token fetching
    this.initTokenFetch();

    this.isEnabled$.subscribe((enabled) => {
      this.#enabled = enabled;
    });

    this.isEnabledV4$.subscribe((enabled) => {
      this.#enabledV4 = enabled;
    });

    this.isSessionLogoutEnabled$.subscribe((enabled) => {
      this.#sessionLogoutEnabled = enabled;
    });

    this.auth0.error$.pipe(withLatestFrom(this.isEnabledV4$)).subscribe(([error, isEnabledV4]) => {
      if (isEnabledV4) {
        this.#errorFlag = true;
        this.errorDescription =
          (error as Error & { error_description?: string }).error_description ||
          error.message ||
          'Unknown error';
      }
    });
  }

  // @linq/feature-flag's internal flags$ is a Subject (not BehaviorSubject) -- it does NOT
  // replay to late subscribers. If anything subscribes to flag$() after LD has already fired
  // onReady, it waits forever for an emission that already happened. Seed each flag with the
  // currently-cached value via startWith so late subscribers always get an immediate emission.
  // shareReplay(1) ensures further subscribers also see the latest value without re-reading
  // the cache (and without re-firing startWith with a stale snapshot).
  isEnabled$ = this.featureFlagService
    .flag$<boolean>(FeatureFlagEnum.Auth0)
    .pipe(startWith(this.featureFlagService.flags?.[FeatureFlagEnum.Auth0] ?? false), shareReplay(1));
  isEnabledV4$ = this.featureFlagService
    .flag$<boolean>(FeatureFlagEnum.Auth0V4)
    .pipe(startWith(this.featureFlagService.flags?.[FeatureFlagEnum.Auth0V4] ?? false), shareReplay(1));

  // ERP-15430 — dedicated gate for the cross-app (V2↔V4) session-logout
  // sync. Kept separate from `isEnabledV4$` so the heartbeat/endSession
  // wiring can be toggled independently of V2's broader Auth0 behavior.
  isSessionLogoutEnabled$ = this.featureFlagService
    .flag$<boolean>(FeatureFlagEnum.V2V4SessionLogout)
    .pipe(startWith(this.featureFlagService.flags?.[FeatureFlagEnum.V2V4SessionLogout] ?? false), shareReplay(1));

  #enabled = false;
  #enabledV4 = false;
  #sessionLogoutEnabled = false;

  getEnabled(): boolean {
    return this.#enabled;
  }

  getEnabledV4(): boolean {
    return this.#enabledV4;
  }

  // ERP-15430 — synchronous accessor for the `v2-v4-session-logout` gate,
  // read by the legacy Frames runtime (`workspace.js`) via `window.authService`
  // to decide whether to suppress its own idle timer in favor of the modern
  // cross-app SessionActivityService.
  getSessionLogoutEnabled(): boolean {
    return this.#sessionLogoutEnabled;
  }

  generateId({ client, user }: { client: string; user: string }) {
    return `${client}-${user}`;
  }

  auth0User$ = this.auth0.user$.pipe(
    map((user): User | null => {
      if (!user) {
        return null;
      }
      const subdomain = this.configService.get('auth0Subdomain');
      const dbUserId = user[ClaimsEnum.ErpUserId];
      const id = this.generateId({ client: subdomain, user: dbUserId });
      const email = user.email || user[ClaimsEnum.Email] || '';
      // Auth0's database connection defaults the standard `name` claim to the email
      // when no separate name was supplied at signup. Treat that case as "no name"
      // so we fall through to the namespaced custom claims and standard OIDC
      // given_name/family_name before giving up to email.
      const stdName = (user.name || '').trim();
      const composedName =
        (stdName && stdName.toLowerCase() !== email.toLowerCase() ? stdName : '') ||
        (user[ClaimsEnum.Name] || '').trim() ||
        `${user[ClaimsEnum.FirstName] || ''} ${user[ClaimsEnum.LastName] || ''}`.trim() ||
        `${user.given_name || ''} ${user.family_name || ''}`.trim() ||
        email;
      return {
        id: id,
        name: composedName,
        email: email,
        admin: user[ClaimsEnum.Admin] === ClaimsValueEnum.SuperUser || user[ClaimsEnum.IsSuperUser] === true,
        // The ERP role is NOT in the JWT -- it lives in
        // adm.users.active_role_id and reaches the browser via framesService.user$
        // (USER_ROLE). Don't reuse the admin claim here -- that produced role='super'.
        // app.component.ts merges in the frames role for downstream consumers.
        role: '',
        sub: user.sub,
        dbUserId: dbUserId,
        tenantId: this.configService.get('tenantId'),
        subdomain: subdomain,
        isAuth0: true,
      };
    }),
    distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
    shareReplay(1),
  );

  framesUser$ = this.framesService.user$.pipe(
    startWith(null),
    map((user): User | null => {
      if (!user) {
        return null;
      }
      const subdomain = this.configService.get('auth0Subdomain');
      const id = this.generateId({ client: subdomain, user: user.USER_ID });
      return {
        id: id,
        name: user.USER_FULL_NAME,
        email: user.EMAIL,
        role: user.USER_ROLE,
        admin: user.USER_ID === 'ADM',
        dbUserId: user.USER_ID,
        tenantId: this.configService.get('tenantId'),
        subdomain: subdomain,
        isAuth0: false,
      };
    }),
    // Compare on id AND role so workspace role switches (same user, new role) propagate to
    // subscribers (e.g. LD context's role attribute in app.component.ts).
    distinctUntilChanged((prev, curr) => prev?.id === curr?.id && prev?.role === curr?.role),
    shareReplay(1),
  );

  user$ = combineLatest([this.auth0User$, this.framesUser$, this.isEnabled$]).pipe(
    map(([auth0User, framesUser, isEnabled]) => {
      if (!isEnabled) return framesUser;
      if (!auth0User) return null;
      // Auth0 mode: identity (name/email/sub) comes from Auth0, but the ERP role
      // (SYS/FAS/ADM/etc.) lives in adm.users.active_role_id and only reaches the
      // browser via framesService.user$.USER_ROLE. Merge it in here so every
      // consumer of user$ sees the right role, not just app.component's LD context.
      return { ...auth0User, role: framesUser?.role ?? '' };
    }),
    shareReplay(1),
  );

  isAuthenticated$ = this.user$.pipe(
    map((user) => !!user),
    distinctUntilChanged(),
    shareReplay(1),
  );

  #accessToken = new BehaviorSubject<string>('');
  #tokenDetails = new BehaviorSubject<any>(null);

  get accessToken(): string {
    return this.#accessToken.value;
  }

  // Observable that emits when access token is available
  accessToken$ = this.#accessToken.asObservable().pipe(
    filter((token) => !!token),
    distinctUntilChanged(),
    shareReplay(1),
  );

  // Observable that indicates if token is ready (includes true/false state)
  hasAccessToken$ = this.#accessToken.asObservable().pipe(
    map((token) => !!token),
    distinctUntilChanged(),
    shareReplay(1),
  );

  #errorFlag = false;
  errorDescription: string | null = null;

  #refreshToken$ = new Subject<boolean>();

  // Single in-flight proactive-refresh timer. Held so it can be replaced rather
  // than accumulated -- see scheduleTokenRefresh().
  #refreshTimer: Subscription | null = null;

  forceNewToken() {
    this.#refreshToken$.next(true);
    this.#refreshToken$.next(false);
  }

  // Eagerly fetch token when authenticated
  private initTokenFetch() {
    combineLatest([
      this.auth0.isAuthenticated$,
      this.isEnabled$,
      this.#refreshToken$.pipe(startWith(false), throttleTime(1000)),
    ])
      .pipe(
        switchMap(([isAuthenticated, isEnabled, refresh]) => {
          if (!isEnabled || !isAuthenticated) {
            this.clearToken();
            return of(null);
          }

          // Handle the failure here rather than letting it reach the outer
          // subscriber. An error propagating out of switchMap TERMINATES this
          // subscription for the rest of the session, so every later
          // forceNewToken() would silently do nothing.
          return this.fetchAccessToken(refresh).pipe(
            catchError(() => {
              this.clearToken();
              return of(null);
            }),
          );
        }),
      )
      .subscribe({
        next: (tokenData) => {
          if (tokenData && typeof tokenData !== 'string') {
            this.#accessToken.next(tokenData.access_token);
            this.#tokenDetails.next(tokenData);
            this.scheduleTokenRefresh(tokenData.expires_in);
          }
        },
        error: () => {
          this.clearToken();
        },
      });
  }

  private clearToken(): void {
    this.#refreshTimer?.unsubscribe();
    this.#refreshTimer = null;
    this.#accessToken.next('');
    this.#tokenDetails.next(null);
  }

  // Schedule one proactive renewal ahead of expiry.
  //
  // This previously subscribed a fresh timer on every successful token fetch and
  // never cancelled the prior one -- and because the subscribe happens in the
  // observer, the outer switchMap does not tear it down either. Timers therefore
  // accumulated over a session and fired renewals at irregular offsets. Keep a
  // single subscription and replace it each time.
  private scheduleTokenRefresh(expiresIn: number): void {
    this.#refreshTimer?.unsubscribe();
    this.#refreshTimer = null;

    if (!expiresIn || expiresIn <= 0) return;

    // Renew 30 min early for a normal 2 h token, but never schedule a
    // non-positive delay -- a short-lived token would otherwise fire the renewal
    // immediately and spin.
    const leadSeconds = Math.min(REFRESH_LEAD_SECONDS, Math.floor(expiresIn / 2));
    this.#refreshTimer = timer((expiresIn - leadSeconds) * 1000).subscribe(() => {
      this.forceNewToken();
    });
  }

  // Kept for backward compatibility - emits token details
  token$ = this.#tokenDetails.asObservable().pipe(shareReplay(1));

  isLoading$ = this.auth0.isLoading$;

  // No popup fallback here, on purpose. `getAccessTokenWithPopup()` calls
  // window.open outside a user gesture, so the browser blocks it -- and with
  // refresh tokens configured correctly the silent path renews against
  // /oauth/token and never needs an interactive fallback. When renewal does
  // genuinely fail, auth0-angular's own setError()/refresh() drives
  // isAuthenticated$ false and LoginComponent takes the redirect, which is the
  // behavior we want.
  private fetchAccessToken(bypassCache = false) {
    return this.auth0
      .getAccessTokenSilently({
        detailedResponse: true,
        cacheMode: bypassCache ? 'off' : 'on',
      })
      .pipe(timeout(10000));
  }

  oidcIsAuthenticated$() {
    return this.auth0.isAuthenticated$;
  }

  authenticate() {
    this.loadingService.show();
    return this.oidcIsAuthenticated$().pipe(
      take(1),
      switchMap((isAuthenticated) => {
        if (isAuthenticated) {
          return this.fetchAccessToken().pipe(
            tap((token) => {
              if (typeof token !== 'string') {
                this.#accessToken.next(token.access_token);
                this.#tokenDetails.next(token);
              } else {
                this.#accessToken.next(token);
                this.#tokenDetails.next({ access_token: token, expires_in: 86400 });
              }
            }),
            map((token) => (typeof token === 'string' ? token : token.access_token)), // Return the new access token
            catchError(() => {
              return of(null); // Return null if token retrieval fails
            }),
          );
        } else {
          if (this.#errorFlag) {
            this.#errorFlag = false; // Reset error flag after handling
            return of(null);
          }
          this.login();
          return of(null); // Return null if loginWithRedirect is triggered
        }
      }),
      finalize(() => {
        this.loadingService.hide();
      }),
    );
  }

  login() {
    this.auth0.loginWithRedirect();
  }

  logout({ skipSessionEnd = false }: { skipSessionEnd?: boolean } = {}) {
    // ERP-15430 — clear the cross-app session cookie server-side BEFORE
    // the Auth0 redirect takes us off-page. Gated on the
    // `v2-v4-session-logout` LaunchDarkly flag — V2 only participates in
    // V4's session-cookie protocol when this flag is on (the rest of the
    // V2 frontend's cross-app wiring lives behind the same gate, e.g.,
    // `AppComponent.initSessionActivitySync()`). When the flag is off,
    // logout is V2-only: no cross-origin call, no impact on a V4
    // session that might be open in another tab.
    //
    // We **await** `endSession()` (via subscription) before calling
    // `auth0.logout(...)` so the DELETE actually leaves the browser
    // before the redirect cancels in-flight requests. Previously this
    // was fire-and-forget, and on slow networks the DELETE was
    // sometimes cancelled mid-flight — V4's cookie stayed alive and
    // V4's next poll didn't detect V2's logout. A failure here is
    // non-fatal (the 30 s poll fallback still catches it eventually,
    // and the cookie expires on its own within 90 minutes).
    //
    // `skipSessionEnd` — when the caller already knows the session is
    // gone (e.g. heartbeat returned 401), skip the endSession call to
    // avoid a redundant 401 and noisy console errors.
    if (this.#sessionLogoutEnabled && !skipSessionEnd) {
      this.sessionActivityService
        .endSession()
        .pipe(finalize(() => this.redirectToAuth0Logout()))
        .subscribe();
    } else {
      this.redirectToAuth0Logout();
    }
  }

  private redirectToAuth0Logout(): void {
    this.auth0.logout({
      logoutParams: {
        returnTo: window.location.href,
      },
    });
  }
}
